<template>
    <div class="flex flex-wrap">
        <div class="w-full mb-3">
            <h1 class="text-3xl text-center">
                {{ itemComponent.product.name }}
                en
                <strong class="text-theme-primary capitalize">
                    {{ itemComponent.merchant.type | merchantInfo("type") }}
                    {{ itemComponent.merchant.name }}
                </strong>
            </h1>
        </div>
        <div v-if="!itemComponent.wasFound" class="w-full px-2 mb-3">
            <div
                class="bg-yellow-400 text-center p-2 rounded-lg border-white border"
            >
                <span class="text-white font-bold">
                    No se registrado el inventario previamente
                </span>
            </div>
        </div>
        <div class="w-full mb-3 flex justify-center items-center">
            <tooltip-button
                v-if="
                    $enabledEcommerceOptions &&
                    $ability.hasPermission('inventory:Active')
                "
                :icon="itemComponent.isEnabled ? '🔴' : '🟢'"
                :message="
                    itemComponent.isEnabled
                        ? 'Desactivar inventario'
                        : 'Activar inventario'
                "
                @click="openActiveModal"
            />
            <tooltip-button
                v-if="$ability.hasPermission('inventory:Delete')"
                icon="🗑️"
                message="Eliminar inventario"
                @click="deleteInventoryComponent"
            />
            <app-button icon circle color="brand-purple" @click="getBaseItem">
                <reload-icon class="text-white h-5 w-5" />
            </app-button>
            <custom-switch
                v-if="
                    $ability.hasPermission('inventory:Update') &&
                    id &&
                    itemComponent.wasFound
                "
                v-model="enabledUpdate"
            >
                <span class="text-theme-inverse">🖊</span>
            </custom-switch>
        </div>

        <div
            class="lg:w-3/12 md:w-4/12 w-full mx-auto flex justify-center items-center"
        >
            <div class="w-60 md:w-28 lg:w-52 h-auto p-0 shadow-md">
                <a
                    class="w-full h-full"
                    :href="`/app/products/detail/${itemComponent.product.id}&tab=references&reference=${itemComponent.id}`"
                    target="_blank"
                    rel="noopener"
                >
                    <img
                        v-img-loading
                        :src="itemComponent.images[0].url | image"
                        :alt="itemComponent.images[0].alt"
                        loading="lazy"
                    />
                </a>
            </div>
        </div>

        <div class="lg:w-9/12 md:w-8/12 w-full mx-auto px-2">
            <div class="mt-4">
                <div class="grid grid-cols-2 xl:grid-cols-3 gap-4">
                    <div
                        class="flex lg:flex-row lg:justify-between flex-col p-3 rounded-lg border border-theme-border shadow-sm hover:shadow-md transition-shadow duration-200"
                    >
                        <div class="flex flex-col w-1/2">
                            <strong
                                class="text-xs uppercase tracking-wide text-theme-muted font-semibold"
                            >
                                Color
                            </strong>
                            <div class="flex items-center gap-2">
                                <span
                                    class="capitalize text-theme-base font-medium"
                                >
                                    {{ itemComponent.color }}
                                </span>
                                <div
                                    class="w-6 h-6 rounded-full border-2 border-theme-border shadow-inner"
                                    :style="{
                                        'background-color':
                                            itemComponent.colorRef
                                    }"
                                ></div>
                            </div>
                        </div>

                        <div class="flex flex-col w-1/2">
                            <strong
                                class="text-xs uppercase tracking-wide text-theme-muted font-semibold"
                            >
                                Tamaño/Talla/Valor
                            </strong>
                            <span
                                class="capitalize text-theme-base font-medium text-lg"
                            >
                                {{ itemComponent.size }}
                            </span>
                        </div>
                    </div>

                    <div
                        class="flex lg:flex-row lg:justify-between flex-col p-3 rounded-lg border border-theme-border shadow-sm hover:shadow-md transition-shadow duration-200"
                    >
                        <div class="flex flex-col">
                            <strong
                                class="text-xs uppercase tracking-wide text-theme-muted font-semibold"
                            >
                                Precios tienda
                            </strong>
                            <div class="flex items-center gap-2">
                                <span
                                    class="text-theme-danger font-semibold text-lg"
                                >
                                    {{
                                        itemComponent.priceOfferMerchant | money
                                    }}
                                </span>
                                <template
                                    v-if="
                                        isOfferPrice(
                                            itemComponent.priceMerchant,
                                            itemComponent.priceOfferMerchant
                                        )
                                    "
                                >
                                    <span class="text-theme-muted">/</span>
                                    <span
                                        class="text-theme-muted line-through text-sm"
                                    >
                                        {{
                                            itemComponent.priceMerchant | money
                                        }}
                                    </span>
                                </template>
                            </div>
                        </div>
                        <div
                            v-if="itemComponent.costMerchant !== undefined"
                            class="flex flex-col"
                        >
                            <strong
                                class="text-xs uppercase tracking-wide text-theme-muted font-semibold"
                            >
                                Margen actual
                            </strong>
                            <span class="text-theme-success font-bold text-2xl">
                                {{ revenue | percent }}
                            </span>
                        </div>
                    </div>

                    <div
                        class="flex items-center justify-center p-3 rounded-lg border border-theme-border shadow-sm hover:shadow-md transition-shadow duration-200"
                    >
                        <strong
                            class="text-2xl uppercase tracking-wide text-theme-muted font-semibold mr-4"
                        >
                            Stock
                        </strong>
                        <span class="text-theme-base font-bold text-4xl">
                            {{ itemComponent.stockMerchant }}
                        </span>
                    </div>

                    <div
                        v-if="$enabledEcommerceOptions"
                        class="flex flex-col p-3 rounded-lg border border-theme-border shadow-sm hover:shadow-md transition-shadow duration-200"
                    >
                        <strong
                            class="text-xs uppercase tracking-wide text-theme-muted font-semibold"
                        >
                            Disponible en {{ $siteType }}
                        </strong>
                        <span class="flex items-center mt-2">
                            <span
                                class="px-3 py-1 rounded-full text-sm font-semibold"
                                :class="
                                    itemComponent.isEnabled
                                        ? 'bg-theme-success text-white'
                                        : 'bg-theme-danger text-white'
                                "
                            >
                                {{
                                    itemComponent.isEnabled ? "Sí 🟢" : "No 🔴"
                                }}
                            </span>
                        </span>
                    </div>

                    <div
                        class="flex flex-col p-3 rounded-lg border border-theme-border shadow-sm hover:shadow-md transition-shadow duration-200"
                    >
                        <strong
                            class="text-xs uppercase tracking-wide text-theme-muted font-semibold"
                        >
                            Enlaces
                        </strong>
                        <div class="flex flex-col text-sm">
                            <div class="flex items-center gap-1">
                                <span class="text-theme-muted font-medium">
                                    Prod:
                                </span>
                                <router-link
                                    :to="`/app/products/detail/${itemComponent.product.id}`"
                                    class="text-xs text-theme-primary hover:opacity-80 hover:underline font-medium transition-colors duration-150"
                                >
                                    {{ itemComponent.product.sku }} ({{
                                        itemComponent.product.id
                                    }})
                                </router-link>
                            </div>
                            <div class="flex items-center gap-1">
                                <span class="text-theme-muted font-medium">
                                    Ref:
                                </span>
                                <router-link
                                    :to="`/app/products/detail/${itemComponent.product.id}?reference=${itemComponent.id}&tab=references`"
                                    class="text-xs text-theme-primary hover:opacity-80 hover:underline font-medium transition-colors duration-150"
                                >
                                    {{ itemComponent.barcode }} ({{
                                        itemComponent.id
                                    }})
                                </router-link>
                            </div>
                        </div>
                    </div>

                    <div
                        class="flex flex-col p-3 rounded-lg border border-theme-border shadow-sm hover:shadow-md transition-shadow duration-200"
                    >
                        <strong
                            class="text-xs uppercase tracking-wide text-theme-muted font-semibold"
                        >
                            Precios {{ $siteType }}
                        </strong>
                        <div class="flex items-center gap-2 mt-2">
                            <span
                                class="text-theme-danger font-semibold text-xl"
                            >
                                {{ itemComponent.priceOffer | money }}
                            </span>
                            <span class="text-theme-muted">/</span>
                            <span class="text-theme-muted line-through text-sm">
                                {{ itemComponent.price | money }}
                            </span>
                        </div>
                    </div>

                    <fragment
                        v-if="
                            $hasInternalCommissions &&
                            itemComponent.isEnabled &&
                            itemComponent.priceAgreedMerchant
                        "
                    >
                        <div
                            class="flex flex-col p-3 bg-theme-hover rounded-lg border border-theme-border shadow-sm hover:shadow-md transition-shadow duration-200"
                        >
                            <strong
                                class="text-xs uppercase tracking-wide text-theme-info font-semibold"
                            >
                                Monto recibido marketplace
                            </strong>
                            <span class="text-theme-info font-bold text-xl">
                                {{ itemComponent.priceAgreedMerchant | money }}
                            </span>
                        </div>
                        <div
                            class="flex flex-col p-3 bg-theme-hover rounded-lg border border-theme-border shadow-sm hover:shadow-md transition-shadow duration-200"
                        >
                            <strong
                                class="text-xs uppercase tracking-wide text-theme-info font-semibold"
                            >
                                Margen marketplace
                            </strong>
                            <span class="text-theme-info font-bold text-xl">
                                {{ revenueMarketplace | percent }}
                            </span>
                        </div>
                    </fragment>
                </div>
            </div>
        </div>

        <div class="w-full mt-6 px-2">
            <price-offer-fields
                v-model="merchantPrices"
                :enabled="enabledUpdate"
                :show-cost="itemComponent.costMerchant !== undefined"
                :show-errors="introValidators.priceOfferMerchant"
                @blur="onBlurMerchantPrices"
                @error-change="pricesHaveError = $event"
            />
        </div>

        <div class="w-full md:w-1/2 mt-6 px-2">
            <custom-input
                v-model="itemComponent.stockMerchant"
                type="number"
                min="0"
                label="Stock en tienda"
                :disabled="!enabledUpdate"
                @blur="introValidators.stockMerchant = true"
            />
        </div>

        <div class="w-full md:w-1/2 mt-6 px-2">
            <custom-input
                v-model="itemComponent.maxStockMerchant"
                label="Stock máximo en tienda"
                type="number"
                min="1"
                :disabled="!enabledUpdate"
            />
        </div>

        <div class="w-full md:w-1/2 mt-6 px-2">
            <custom-input
                v-model="itemComponent.minStockMerchant"
                min="0"
                type="number"
                label="Stock mínimo en tienda"
                :disabled="!enabledUpdate"
            />
        </div>

        <div class="w-full md:w-1/2 mt-6 px-2">
            <checkbox-selector
                v-model="itemComponent.alwaysStockMerchant"
                label="Siempre en inventario"
                :enabled="enabledUpdate"
            />
        </div>

        <div class="w-full flex justify-center my-5">
            <app-button
                v-if="
                    $ability.hasPermission('inventory:Update') && enabledUpdate
                "
                :loading="isLoading"
                @click="updateInventoryComponent"
            >
                {{ itemComponent.wasFound ? "Actualizar" : "Registrar" }}
                inventario
            </app-button>
        </div>

        <app-dialog v-model="isActiveModal">
            <template #header>
                <h2 class="text-theme-primary text-2xl">
                    Deseas
                    {{ itemComponent.isEnabled ? "desactivar" : "activar" }}
                    el inventario ?
                </h2>
            </template>
            <div class="mx-2">
                <div v-if="itemComponent.isEnabled" class="w-10/12 mx-auto">
                    <span>
                        Vas a desactivar el inventario de la referencia ({{
                            itemComponent.id
                        }}) para el tienda{{ itemComponent.merchant.name }}.
                        Ahora NO se podrá comprar el inventario del tienda desde
                        el {{ $siteType }}.
                    </span>
                </div>
                <div v-else>
                    <span>
                        Vas a activar el inventario de la referencia ({{
                            itemComponent.id
                        }}) para tienda{{ itemComponent.merchant.name }}. Ahora
                        se podrá comprar el inventario del tiendadesde el
                        {{ $siteType }}.
                    </span>
                </div>
                <div class="mt-3 flex justify-center">
                    <app-button @click="toggleActiveInventory">
                        {{ itemComponent.isEnabled ? "Desactivar" : "Activar" }}
                    </app-button>
                </div>
            </div>
        </app-dialog>
    </div>
</template>

<script>
import { mapActions, mapGetters, mapState } from "vuex";

import {
    DEFAULT_FRANCHISE_MARGIN,
    DEFAULT_IMAGE_URL
} from "@/common/constants";
import { isOfferPrice } from "@/common/lib/price-offer";

import ReloadIcon from "@/components/icons/ReloadIcon.vue";
import AppDialog from "@/components/utils/AppDialog.vue";
import CheckboxSelector from "@/components/utils/CheckboxSelector.vue";
import CustomInput from "@/components/utils/CustomInput.vue";
import PriceOfferFields from "@/components/utils/PriceOfferFields.vue";
import TooltipButton from "@/components/utils/TooltipButton.vue";

export default {
    name: "DetailInventory",
    components: {
        AppDialog,
        ReloadIcon,
        CheckboxSelector,
        PriceOfferFields,
        TooltipButton,
        CustomInput
    },
    data: () => ({
        id: null,
        $suggestMarginMarketplace: 1 - DEFAULT_FRANCHISE_MARGIN / 100,
        revenue: 0,
        revenueMarketplace: 0,
        isLoading: false,
        enabledUpdate: false,
        isActiveModal: false,
        introValidators: {
            stockMerchant: false,
            priceOfferMerchant: false
        },
        pricesHaveError: false,
        merchant: { merchantId: "", merchantType: "" },
        itemComponent: {
            priceOffer: 1000,
            priceAgreedMerchant: 0,
            price: 1000,
            id: 1,
            size: "Unico",
            color: "blanco",
            colorRef: "#fff",
            stock: 1,
            barcode: "",
            merchant: {
                id: "",
                name: ""
            },
            product: {
                id: 1,
                name: "Cargando",
                slug: "cargando"
            },
            images: [
                {
                    id: 1,
                    url: DEFAULT_IMAGE_URL,
                    alt: "alt"
                }
            ],
            isEnabled: false,
            costMerchant: 700,
            maxStockMerchant: 100,
            minStockMerchant: 0,
            priceOfferMerchant: 1000,
            priceMerchant: 1000,
            stockMerchant: 0,
            alwaysStockMerchant: false,
            wasFound: false
        }
    }),
    computed: {
        ...mapState("commons", ["selectedMerchant"]),
        ...mapGetters("control", ["backgroundColor"]),
        merchantPrices: {
            get() {
                return {
                    price: this.itemComponent.priceMerchant,
                    priceOffer: this.itemComponent.priceOfferMerchant,
                    cost: this.itemComponent.costMerchant
                };
            },
            set({ cost, price, priceOffer }) {
                this.itemComponent.priceMerchant = price;
                this.itemComponent.priceOfferMerchant = priceOffer;
                this.itemComponent.costMerchant = cost;
            }
        }
    },
    watch: {
        selectedMerchant(value) {
            if (this.id && !this.isLoading) {
                this.merchant = value;
                this.getBaseItem();
            }
        }
    },
    async mounted() {
        const verifyPagePermission =
            this.$ability.verifyPagePermission.bind(this);
        if (!(await verifyPagePermission("inventory:Get", "/app/inventories")))
            return;
        const { id, merchantId, merchantType } = this.$route.params;
        this.id = parseInt(id);
        if (merchantId && merchantType) {
            this.merchant = { merchantId, merchantType };
        } else {
            this.merchant = this.selectedMerchant;
        }
        this.getBaseItem();
    },
    methods: {
        isOfferPrice,
        ...mapActions("inventories", [
            "getInventory",
            "updateInventory",
            "deleteInventory",
            "activeInventoryReference",
            "listInventoryByReference"
        ]),
        async getBaseItem() {
            this.isLoading = true;
            this.$loading.show();
            try {
                this.itemComponent = await this.getInventory({
                    referenceId: this.id,
                    merchantId: this.merchant.merchantId,
                    merchantType: this.merchant.merchantType
                });
                this.enabledUpdate = !this.itemComponent.wasFound;
                this.calculateRevenue();
                this.isLoading = false;
                this.$loading.hide();
            } catch (error) {
                this.isLoading = false;
                this.$loading.hide();
                this.isLoading = false;
                await this.$swal.fire({
                    background: this.backgroundColor,
                    title: error.title,
                    text: error.message,
                    icon: error.icon
                });
                if (error.statusCode === 404) {
                    this.$router.push("/app");
                }
            }
        },
        async updateInventoryComponent() {
            if (!this.validateInventory()) {
                return;
            }
            this.isLoading = true;
            try {
                const {
                    alwaysStockMerchant,
                    costMerchant,
                    id,
                    maxStockMerchant,
                    minStockMerchant,
                    priceMerchant,
                    priceOfferMerchant,
                    stockMerchant
                } = this.itemComponent;
                await this.updateInventory({
                    merchantId: this.itemComponent.merchant.id,
                    merchantType: this.itemComponent.merchant.type,
                    referenceId: id,
                    stockMerchant,
                    maxStockMerchant,
                    minStockMerchant,
                    priceOfferMerchant,
                    costMerchant,
                    priceMerchant,
                    alwaysStockMerchant
                });

                this.itemComponent.wasFound = true;

                this.$sound.play();
                this.$swal.fire({
                    background: this.backgroundColor,
                    title: "Actualización exitosa",
                    icon: "success",
                    showCloseButton: true,
                    timer: 3000
                });
                this.isLoading = false;
            } catch (error) {
                this.isLoading = false;
                this.$sound.play("error");
                this.$swal.fire({
                    background: this.backgroundColor,
                    title: error.title,
                    text: error.message,
                    icon: error.icon
                });
            }
        },
        async deleteInventoryComponent() {
            const dialogResult = await this.$swal.fire({
                background: this.backgroundColor,
                title: "Vas a eliminar una referencia de tu inventario ?",
                text: `La referencia no volverá a aparecer en la vista de inventario y virtualmente tendrás inventario en 0 para la referencia ${this.itemComponent.id}`,
                icon: "warning",
                showCancelButton: true,
                confirmButtonColor: "#02913f",
                cancelButtonColor: "#3085d6",
                confirmButtonText: "Si",
                cancelButtonText: "No"
            });
            if (dialogResult.isConfirmed) {
                this.isLoading = true;
                this.$loading.show();
                try {
                    await this.deleteInventory({
                        referenceId: this.itemComponent.id,
                        merchantType: this.itemComponent.merchantType,
                        merchantId: this.itemComponent.merchantId
                    });

                    this.itemComponent = {
                        ...this.itemComponent,
                        stockMerchant: 0,
                        isEnabled: false,
                        maxStockMerchant: 0,
                        minStockMerchant: 0,
                        costMerchant: 0,
                        priceMerchant: 0,
                        priceOfferMerchant: 0
                    };
                    this.$loading.hide();
                    this.isLoading = false;
                    this.$router.push("/app/inventories");
                } catch (error) {
                    this.$loading.hide();
                    this.isLoading = false;
                    this.$swal.fire({
                        background: this.backgroundColor,
                        title: error.title,
                        text: error.message,
                        icon: error.icon
                    });
                    this.$sound.play("error");
                }
            }
        },
        async toggleActiveInventory() {
            this.isActiveModal = false;
            this.isLoading = true;
            this.$loading.show();
            try {
                await this.activeInventoryReference({
                    referenceId: this.itemComponent.id,
                    merchantId: this.itemComponent.merchant.id,
                    merchantType: this.itemComponent.merchant.type,
                    isEnabled: !this.itemComponent.isEnabled
                });
                this.itemComponent.isEnabled = !this.itemComponent.isEnabled;
                this.calculateRevenue();
                this.$sound.play();
                this.calculateRevenue();
                this.$swal.fire({
                    background: this.backgroundColor,
                    title: "Actualización exitosa",
                    icon: "success",
                    showCloseButton: true,
                    timer: 3000
                });
                this.$loading.hide();
                this.isLoading = false;
            } catch (error) {
                this.$loading.hide();
                this.isLoading = false;
                this.$loading.hide();
                this.$swal.fire({
                    background: this.backgroundColor,
                    title: error.title,
                    text: error.message,
                    icon: error.icon
                });
                this.$sound.play("error");
            }
        },
        openActiveModal() {
            this.isActiveModal = true;
        },
        validateInventory() {
            this.introValidators = {
                stockMerchant: true,
                priceOfferMerchant: true
            };
            if (
                this.itemComponent.stockMerchant < 0 ||
                this.itemComponent.maxStockMerchant < 1 ||
                this.itemComponent.minStockMerchant < 0 ||
                !this.itemComponent.priceOfferMerchant ||
                !this.itemComponent.priceMerchant ||
                this.pricesHaveError
            ) {
                this.$swal.fire({
                    background: this.backgroundColor,
                    title: "Inventario invalido",
                    text: "Revisa por favor que todo este completo",
                    icon: "info"
                });
                return false;
            }
            return true;
        },

        onBlurMerchantPrices() {
            this.introValidators.priceOfferMerchant = true;
            this.calculateRevenue();
        },
        calculateRevenue() {
            this.revenue =
                1 -
                this.itemComponent.costMerchant /
                    this.itemComponent.priceOfferMerchant;
            if (this.itemComponent.priceAgreedMerchant) {
                this.revenueMarketplace =
                    1 -
                    this.itemComponent.costMerchant /
                        this.itemComponent.priceAgreedMerchant;
            }
        }
    }
};
</script>
